import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { RuntimeConfigService } from '../services/runtime-config.service';

const AUTH_RETRY_HEADER = 'X-Auth-Retry';

export const authInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> => {
    const authService = inject(AuthService);
    const runtimeConfigService = inject(RuntimeConfigService);

    // Only add token for API requests
    if (req.url.startsWith(runtimeConfigService.getApiBasePath() || '')) {
        // Check if user is authenticated before attempting token acquisition
        if (!authService.isAuthenticated()) {
            console.warn('User not authenticated, skipping token acquisition for:', req.url);
            return next(req);
        }

        return authService.getAccessTokenWithRetry().pipe(
            switchMap(token => {
                const authReq = token
                    ? req.clone({
                          setHeaders: {
                              Authorization: `Bearer ${token}`,
                          },
                      })
                    : req;

                return next(authReq).pipe(
                    catchError((error: unknown) => {
                        if (!(error instanceof HttpErrorResponse) || error.status !== 401 || req.headers.has(AUTH_RETRY_HEADER)) {
                            return throwError(() => error);
                        }

                        // Token may have expired between acquisition and the response — force renew once and retry.
                        return authService.checkAndRefreshToken().pipe(
                            switchMap(() => authService.getAccessTokenWithRetry()),
                            switchMap(newToken => {
                                const retryReq = req.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${newToken}`,
                                        [AUTH_RETRY_HEADER]: '1',
                                    },
                                });
                                return next(retryReq);
                            })
                        );
                    })
                );
            }),
            catchError(error => {
                console.error('Token acquisition error:', error);
                // If token acquisition fails, the auth service will handle logout
                throw error;
            })
        );
    }

    return next(req);
};
